"use client";

import { Ban, Play, RefreshCcw } from "lucide-react";
import type { MouseEvent, ReactNode } from "react";
import type { AdminNodeAction, NodeExecutionDirective } from "@/types/aicheck";
import type { PipelineNodeStatus } from "../../types";

interface AdminNodeActionsProps {
  nodeId?: string;
  nodeStatus: PipelineNodeStatus;
  nodeExecutionDirectives?: NodeExecutionDirective[];
  onAdminAction?: (action: AdminNodeAction, nodeId: string) => void;
}

function IconButton({
  label,
  className,
  onClick,
  children,
}: {
  label: string;
  className: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`nodrag inline-flex h-6 w-6 items-center justify-center rounded border transition-colors ${className}`}
      title={label}
      aria-label={label}
    >
      {children}
    </button>
  );
}

export function AdminNodeActions({
  nodeId,
  nodeStatus,
  nodeExecutionDirectives,
  onAdminAction,
}: AdminNodeActionsProps) {
  if (!nodeId || typeof onAdminAction !== "function") return null;
  if (nodeId === "sys-fetch") return null;

  const directive = (nodeExecutionDirectives ?? []).find(
    (entry) => entry.rootNodeId === nodeId,
  );
  const showResume =
    (nodeStatus === "planned" ||
      nodeStatus === "pending" ||
      nodeStatus === "blocked") &&
    directive?.state === "scheduled";
  const canToggleAbortFromHere =
    showResume ||
    ((nodeStatus === "planned" || nodeStatus === "pending") &&
      (!directive || directive.state === "scheduled"));
  const showAbort = nodeStatus === "active";
  const showRerun = nodeStatus !== "active" && directive?.state !== "stale";
  const staleBadge = directive?.state === "stale";

  if (!showAbort && !canToggleAbortFromHere && !showRerun && !staleBadge) {
    return null;
  }

  return (
    <div className="mt-1 flex items-center gap-1">
      {showRerun && (
        <IconButton
          label="Rerun from here"
          className="border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-slate-100"
          onClick={(event) => {
            event.stopPropagation();
            onAdminAction("rerun_from_node", nodeId);
          }}
        >
          <RefreshCcw size={12} />
        </IconButton>
      )}
      {showAbort && (
        <IconButton
          label="Abort this node and block its descendants"
          className="border-orange-200 bg-orange-50 text-orange-700 hover:border-orange-300 hover:bg-orange-100"
          onClick={(event) => {
            event.stopPropagation();
            onAdminAction("abort_node", nodeId);
          }}
        >
          <Ban size={12} />
        </IconButton>
      )}
      {canToggleAbortFromHere && (
        <IconButton
          label={
            showResume
              ? "Resume this subtree"
              : "Abort from here and block this subtree"
          }
          className={
            showResume
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100"
              : "border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-300 hover:bg-amber-100"
          }
          onClick={(event) => {
            event.stopPropagation();
            onAdminAction("toggle_abort_from_here", nodeId);
          }}
        >
          {showResume ? <Play size={12} /> : <Ban size={12} />}
        </IconButton>
      )}
      {staleBadge && (
        <span
          className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-500"
          title="This subtree abort was restored from browser checkpoint only and is no longer active on the server."
        >
          stale
        </span>
      )}
    </div>
  );
}
